import { PhoneMask } from './phoneMask.js';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
let registrationSettingsPromise;

export const isValidEmail = (value) => EMAIL_PATTERN.test(value.trim());

export const isValidPhoneFallback = (value) => {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 5 && digits.length <= 15;
};

export const buildRegistrationUrl = (baseUrl, email, phone = '') => {
  const url = new URL(baseUrl);
  url.searchParams.set('email', email.trim());

  if (phone.trim()) {
    url.searchParams.set('phone', phone.trim());
  } else {
    url.searchParams.delete('phone');
  }

  return url.toString();
};

const setFieldError = (field, message = '') => {
  const control = field.querySelector('.js-inline-registration-control');
  const input = field.querySelector('input');
  const error = field.querySelector('.inline-registration__error');
  const hasError = Boolean(message);

  field.classList.toggle('inline-registration__field--invalid', hasError);
  if (input) input.setAttribute('aria-invalid', String(hasError));
  if (control && control !== input) control.setAttribute('aria-invalid', String(hasError));
  if (error) error.textContent = message;
};

const loadRegistrationSettings = (url) => {
  if (!registrationSettingsPromise) {
    registrationSettingsPromise = fetch(url, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Registration settings request failed: ${response.status}`);
        return response.json();
      });
  }
  return registrationSettingsPromise;
};

export const applyRegistrationSettings = (form, settings) => {
  const phoneInput = form.querySelector('.js-inline-registration-phone');
  const phoneField = phoneInput?.closest('.js-inline-registration-field');
  const phoneLabel = phoneField?.querySelector('.inline-registration__label');
  const showPhone = Boolean(settings.is_show_phone);
  const requirePhone = showPhone && Boolean(settings.is_phone_required);

  if (phoneField) phoneField.hidden = !showPhone;
  if (phoneInput) {
    phoneInput.disabled = !showPhone;
    phoneInput.required = requirePhone;
  }
  phoneLabel?.classList.toggle('inline-registration__label--required', requirePhone);
};

const initForm = (form) => {
  const emailInput = form.querySelector('.js-inline-registration-email');
  const phoneInput = form.querySelector('.js-inline-registration-phone');
  const privacyInput = form.querySelector('.js-inline-registration-privacy');
  const emailField = emailInput.closest('.js-inline-registration-field');
  const phoneField = phoneInput.closest('.js-inline-registration-field');
  const privacyField = privacyInput.closest('.js-inline-registration-field');
  const errors = form.dataset;
  const phoneMask = window.intlTelInput
    ? new PhoneMask(phoneInput, {
      showValidation: false
    })
    : null;

  const validateEmail = () => {
    const value = emailInput.value.trim();
    const message = !value
      ? errors.errorRequired
      : (!isValidEmail(value) ? errors.errorEmail : '');
    setFieldError(emailField, message);
    return !message;
  };

  const validatePhone = () => {
    if (phoneInput.disabled) return true;

    const value = phoneInput.value.trim();
    const valid = value
      ? (phoneMask ? phoneMask.validate(false) : isValidPhoneFallback(value))
      : !phoneInput.required;
    const message = !value && phoneInput.required ? errors.errorRequired : errors.errorPhone;
    setFieldError(phoneField, valid ? '' : message);
    return valid;
  };

  const validatePrivacy = () => {
    const message = privacyInput.checked ? '' : errors.errorPrivacy;
    setFieldError(privacyField, message);
    return !message;
  };

  emailInput.addEventListener('input', validateEmail);
  emailInput.addEventListener('blur', validateEmail);
  phoneInput.addEventListener('input', validatePhone);
  phoneInput.addEventListener('blur', validatePhone);
  privacyInput.addEventListener('change', validatePrivacy);

  loadRegistrationSettings(form.dataset.registrationSettingsUrl)
    .then((settings) => applyRegistrationSettings(form, settings))
    .catch((error) => console.error(error));

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    const isValid = [validateEmail(), validatePhone(), validatePrivacy()].every(Boolean);
    if (!isValid) {
      form.querySelector('input[aria-invalid="true"]')?.focus();
      return;
    }

    const phone = !phoneInput.disabled && phoneInput.value.trim() && phoneMask
      ? phoneMask.getFormattedNumber()
      : (!phoneInput.disabled ? phoneInput.value : '');

    window.location.assign(buildRegistrationUrl(form.action, emailInput.value, phone));
  });
};

export default function inlineRegistration () {
  document.querySelectorAll('.js-inline-registration').forEach(initForm);
}
